import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { routeUrl } from "@/app/routes";
import { useOnboardingStatus } from "@/features/home/onboarding/hooks/use-onboarding-status";
import { OnboardingImportDialog } from "@/features/home/onboarding/onboarding-import.dialog";
import { shouldShowImportOnboarding } from "@/features/home/onboarding/onboarding-import.logic";
import { useCheckInLauncher } from "@/features/home/onboarding/use-check-in-launcher";
import { useImportOnboardingNudge } from "@/features/home/onboarding/use-import-onboarding-nudge";
import { useOnboardingAnalytics } from "@/features/home/onboarding/use-onboarding-analytics";
import { CreateKudosDialog } from "@/features/kudos/components/create-kudos-dialog";
import { AddUsersDialog } from "@/features/people/components/add-users-dialog";
import { useAuth } from "@/shared/auth/auth.context";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

interface OnboardingContextValue {
  openImportTeam: () => void;
  openGiveKudos: () => void;
  openCheckIn: () => void;
  suppressFirstRunBanner: boolean;
}

function missingOnboardingProvider(): never {
  throw new Error("useOnboardingActions must be used inside an <OnboardingProvider>");
}

const OnboardingContext = createContext<OnboardingContextValue>({
  openImportTeam: missingOnboardingProvider,
  openGiveKudos: missingOnboardingProvider,
  openCheckIn: missingOnboardingProvider,
  suppressFirstRunBanner: false,
});

export function OnboardingProvider({ children }: { children: ReactNode }): ReactNode {
  const { workspace } = useActiveWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const queryClient = useQueryClient();
  const { launchCheckIn, isCheckInOpen } = useCheckInLauncher();
  const onboarding = useOnboardingStatus(workspace.id);
  const { snoozed, remindLater } = useImportOnboardingNudge(workspace.id);
  const [addUsersOpen, setAddUsersOpen] = useState(false);
  const [giveKudosOpen, setGiveKudosOpen] = useState(false);
  const [autoClosed, setAutoClosed] = useState(false);
  const [celebrateOpen, setCelebrateOpen] = useState(false);

  const canLaunchActions = !workspace.isReadOnly;
  const lastWorkspaceRef = useRef(workspace.id);
  const checkInWasOpenRef = useRef(false);
  useOnboardingAnalytics({
    userId: user?.id ?? null,
    workspaceId: workspace.id,
    status: onboarding.data,
  });

  useEffect(() => {
    if (lastWorkspaceRef.current === workspace.id) return;
    lastWorkspaceRef.current = workspace.id;
    setAutoClosed(false);
    setCelebrateOpen(false);
  }, [workspace.id]);

  // The self-check-in dialog lives in the topbar and does not invalidate the
  // onboarding query itself, so refresh status when it closes to let the hub
  // reflect the completed check-in (and close once all three steps are done).
  useEffect(() => {
    if (checkInWasOpenRef.current && !isCheckInOpen) {
      void queryClient.invalidateQueries({ queryKey: ["onboarding", workspace.id] });
    }
    checkInWasOpenRef.current = isCheckInOpen;
  }, [isCheckInOpen, queryClient, workspace.id]);

  const importOnboardingOpen = shouldShowImportOnboarding({
    canLaunchActions,
    autoClosed,
    snoozed,
    isWorkspaceHome: location.pathname === routeUrl.workspace.home(workspace.slug),
    onboardingStatus: onboarding.data,
  });
  // While a sub-action dialog (kudos, check-in, or the standalone add-users
  // flow) is open, the hub steps aside to avoid stacked dialogs. It reappears
  // when the sub-dialog closes, until all three steps are complete.
  const subDialogOpen = giveKudosOpen || addUsersOpen || isCheckInOpen;
  const importDialogOpen = (importOnboardingOpen || celebrateOpen) && !subDialogOpen;
  const welcomeMessage = useMemo(() => {
    const firstName = user?.firstName?.trim();
    const displayName = user?.displayName?.trim();
    const name = firstName || displayName;
    return name
      ? `Welcome to Pulsewise, ${name}. Let's get your first signal moving.`
      : "Welcome to Pulsewise. Let's get your first signal moving.";
  }, [user?.displayName, user?.firstName]);

  const openImportTeam = useCallback(() => {
    if (!canLaunchActions) return;
    setAddUsersOpen(true);
  }, [canLaunchActions]);

  const openGiveKudos = useCallback(() => {
    if (!canLaunchActions) return;
    setGiveKudosOpen(true);
  }, [canLaunchActions]);

  const openCheckIn = useCallback(() => {
    if (!canLaunchActions) return;
    launchCheckIn();
  }, [canLaunchActions, launchCheckIn]);

  const handleAddUsersOpenChange = useCallback(
    (open: boolean) => {
      setAddUsersOpen(open);
      if (!open) {
        void queryClient.invalidateQueries({ queryKey: ["onboarding", workspace.id] });
      }
    },
    [queryClient, workspace.id],
  );

  const handleImported = useCallback(() => {
    setCelebrateOpen(true);
    void queryClient.invalidateQueries({ queryKey: ["onboarding", workspace.id] });
  }, [queryClient, workspace.id]);

  const handleStandaloneImported = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["onboarding", workspace.id] });
  }, [queryClient, workspace.id]);

  const handleGiveKudosOpenChange = useCallback((open: boolean) => {
    setGiveKudosOpen(open);
  }, []);

  const handleKudosSuccess = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["onboarding", workspace.id] });
    void navigate({ to: routeUrl.kudos.list(workspace.slug) });
  }, [navigate, queryClient, workspace.id, workspace.slug]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      openImportTeam,
      openGiveKudos,
      openCheckIn,
      suppressFirstRunBanner: importDialogOpen,
    }),
    [importDialogOpen, openImportTeam, openGiveKudos, openCheckIn],
  );

  return (
    <OnboardingContext value={value}>
      {children}
      {canLaunchActions ? (
        <>
          <OnboardingImportDialog
            workspaceId={workspace.id}
            open={importDialogOpen}
            onboardingStatus={onboarding.data}
            welcomeMessage={welcomeMessage}
            // Dismissing (Escape, outside click, the X) snoozes exactly
            // like the explicit "Remind me later" button. It used to set
            // only `autoClosed`, which is React state, so the dismissal died
            // with the page: the hub returned on every reload and every
            // workspace switch, and the ONLY durable way out was finding the
            // one button. Closing a dialog is a user saying "not now", and
            // the app was discarding it.
            //
            // Radix fires `onOpenChange` for user-initiated closes only; a
            // parent-driven `open` change (a sub-dialog taking over, all
            // steps completing) does not reach here, so this cannot snooze
            // the hub on someone's behalf.
            onOpenChange={(open) => {
              if (open) return;
              remindLater();
              setCelebrateOpen(false);
              setAutoClosed(true);
            }}
            onRemindLater={() => {
              remindLater();
              setCelebrateOpen(false);
              setAutoClosed(true);
            }}
            onImported={handleImported}
            onStartKudos={openGiveKudos}
            onStartCheckIn={openCheckIn}
            onDoneCelebrating={() => {
              // Keep the hub open so the remaining steps (kudos, check-in)
              // stay actionable; it only auto-closes once every step is done.
              setCelebrateOpen(false);
            }}
          />
          <AddUsersDialog
            workspaceId={workspace.id}
            open={addUsersOpen}
            onOpenChange={handleAddUsersOpenChange}
            onImported={handleStandaloneImported}
          />
          <CreateKudosDialog
            workspaceId={workspace.id}
            open={giveKudosOpen}
            onOpenChange={handleGiveKudosOpenChange}
            onSuccess={handleKudosSuccess}
          />
        </>
      ) : null}
    </OnboardingContext>
  );
}

export function useOnboardingActions(): OnboardingContextValue {
  return use(OnboardingContext);
}
